/**
 * Карточка сводки вкладки «Голоса клиента» (#2060, спека
 * 2026-10-11-voice-report-layers.md §2.1, §7): что главное, сколько
 * разговоров, сдвиг той же строки к прошлому периоду и знаменатель словами.
 * Клик открывает раздел карточки; карточка открытого раздела подсвечена.
 *
 * Набрана как карточки единого дашборда: заголовок `text-xs`, название
 * `text-sm font-medium`, число `text-2xl font-light`.
 *
 * Спарклайн — ряд той же строки из ответа её блока (#2553): своего запроса у
 * карточки нет, ряд приходит, когда раздел строки открыт или был открыт, и
 * не старше ответа счёта карточки (#2712).
 */
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useVoiceLoadedRowSeries } from './useVoiceSeries';
import type { SummaryCardState } from './voice-summary-view-model';
import { useVoiceComparable } from './voice-theme-filter-context';
import { VoiceSparkline } from './VoiceSparkline';

interface VoiceSummaryCardProps {
  title: string;
  state: SummaryCardState;
  active: boolean;
  onOpen: () => void;
  /** Its place in the summary grid (`VoiceLayers`). */
  className?: string;
}

export function VoiceSummaryCard({
  title,
  state,
  active,
  onOpen,
  className,
}: VoiceSummaryCardProps) {
  const { t } = useTranslation('dashboard');
  const lead = state.kind === 'ready' ? state.lead : null;
  // No change under a theme filter without a comparable previous window (#2229).
  const trend = useVoiceComparable() ? lead?.trend : undefined;
  // An objection's change of zero says nothing; a voice row's mark is already
  // the rule's verdict (Р3).
  const shift =
    (trend?.kind === 'change' && trend.delta !== 0) ||
    trend?.kind === 'up' ||
    trend?.kind === 'down'
      ? trend
      : null;
  const spark = useVoiceLoadedRowSeries(
    lead?.seriesRef ?? null,
    lead?.calls ?? 0,
    state.kind === 'ready' ? state.answer : undefined
  );
  // The card's share is of recognized objections, the line's — of every
  // analysed conversation: said in words, not only in the line's tooltip.
  const objectionLine = spark !== null && lead?.seriesRef?.signal === 'objection';

  return (
    <button
      type="button"
      onClick={onOpen}
      // Not a toggle: the card opens its section, and the open one is current.
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex flex-col gap-1 rounded-card border bg-bg-surface p-4 text-left transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-primary' : 'border-border',
        className
      )}
    >
      <span className="text-xs text-fg-muted">{title}</span>
      {lead ? (
        <>
          <span className="truncate text-sm font-medium text-fg">{lead.name}</span>
          <span className="flex flex-wrap items-baseline gap-2">
            <span className="text-2xl font-light tabular-nums text-primary">
              {lead.calls}
            </span>
            {trend?.kind === 'new' && (
              <Badge variant="info">{t('voicePage.trend.new')}</Badge>
            )}
            {trend?.kind === 'gone' && (
              <Badge variant="default">{t('voicePage.trend.gone')}</Badge>
            )}
            {shift && (
              <span
                className="font-mono text-xs text-fg-muted"
                title={
                  lead.prevShareKey
                    ? t(lead.prevShareKey, { share: shift.prevShare })
                    : undefined
                }
              >
                {t(shift.delta > 0 ? 'voicePage.trend.up' : 'voicePage.trend.down', {
                  delta: Math.abs(shift.delta),
                })}
              </span>
            )}
            {spark && (
              <VoiceSparkline
                series={spark.series}
                unit={spark.unit}
                // The card is narrower than a row: the line keeps its place.
                className="ml-auto block self-center"
                // The card's name is what it opens; the line is its tooltip.
                decorative
              />
            )}
          </span>
          <span className="text-xs text-fg-muted">{lead.basis}</span>
          {objectionLine && (
            <span className="text-xs text-fg-muted">
              {t('voicePage.layers.summary.objectionLineBase')}
            </span>
          )}
        </>
      ) : (
        <span className="text-sm text-fg-muted">
          {state.kind === 'loading'
            ? t('voicePage.layers.summary.loading')
            : state.kind === 'failed'
              ? t('voicePage.layers.summary.failed')
              : t('voicePage.layers.summary.thin')}
        </span>
      )}
    </button>
  );
}
